/*
 * Brand menu.
 *
 * The brand list lives in sections/brand-menu.liquid and is pulled in through
 * the Section Rendering API the first time a shopper opens the menu. The markup
 * is cached for the rest of the session so later opens are instant.
 *
 * Everything is delegated off `document`, so the header re-rendering (theme
 * editor, section reload) does not need a re-init.
 */
(function () {
  'use strict';

  var SECTION_ID = 'brand-menu';
  var CACHE_KEY = 'uhm:brand-menu:v1';

  var memoryCache = null;
  var inFlight = null;

  function sectionUrl() {
    var root = '/';
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      root = window.Shopify.routes.root;
    }
    return root + '?section_id=' + SECTION_ID;
  }

  function readCache() {
    if (memoryCache) return memoryCache;
    try {
      return sessionStorage.getItem(CACHE_KEY) || null;
    } catch (e) {
      return null;
    }
  }

  function writeCache(html) {
    memoryCache = html;
    try {
      sessionStorage.setItem(CACHE_KEY, html);
    } catch (e) {
      /* private mode / quota — memory cache still applies */
    }
  }

  function fetchBrands() {
    var cached = readCache();
    if (cached) return Promise.resolve(cached);
    if (inFlight) return inFlight;

    inFlight = fetch(sectionUrl(), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Brand menu request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var content = parsed.querySelector('[data-brandnav-content]');
        if (!content) throw new Error('Brand menu markup missing');

        inFlight = null;
        writeCache(content.outerHTML);
        return content.outerHTML;
      })
      .catch(function (error) {
        inFlight = null;
        throw error;
      });

    return inFlight;
  }

  function setStatus(root, message) {
    var status = root.querySelector('[data-brandnav-status]');
    var text = root.querySelector('[data-brandnav-status-text]');
    if (!status) return;

    if (message === null) {
      status.hidden = true;
      return;
    }

    status.hidden = false;
    if (text) text.textContent = message;
  }

  function fill(root) {
    var body = root.querySelector('[data-brandnav-body]');
    if (!body || root.dataset.brandnavLoaded === 'true') return;

    root.dataset.brandnavLoaded = 'pending';

    fetchBrands()
      .then(function (html) {
        body.innerHTML = html;
        root.dataset.brandnavLoaded = 'true';
        setStatus(root, null);
      })
      .catch(function () {
        root.dataset.brandnavLoaded = '';
        setStatus(root, 'Brands could not be loaded. Please try again.');
        var spinner = root.querySelector('.brandnav__spinner');
        if (spinner) spinner.remove();
      });
  }

  function close(root) {
    var toggle = root.querySelector('[data-brandnav-toggle]');
    var panel = root.querySelector('[data-brandnav-panel]');
    if (!panel) return;

    root.classList.remove('brandnav--open');
    panel.hidden = true;
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  function closeAll(except) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-brandnav]'), function (root) {
      if (root !== except) close(root);
    });
  }

  function open(root) {
    var toggle = root.querySelector('[data-brandnav-toggle]');
    var panel = root.querySelector('[data-brandnav-panel]');
    if (!panel) return;

    closeAll(root);
    root.classList.add('brandnav--open');
    panel.hidden = false;
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    fill(root);
  }

  function toggle(root) {
    if (root.classList.contains('brandnav--open')) {
      close(root);
    } else {
      open(root);
    }
  }

  function filterBrands(root, term) {
    var query = term.trim().toLowerCase();
    var groups = root.querySelectorAll('[data-brandnav-group]');
    var noMatch = root.querySelector('[data-brandnav-no-match]');
    var visible = 0;

    Array.prototype.forEach.call(groups, function (group) {
      var shown = 0;

      Array.prototype.forEach.call(group.querySelectorAll('[data-brandnav-item]'), function (item) {
        var name = item.getAttribute('data-brandnav-name') || '';
        var hit = query === '' || name.indexOf(query) !== -1;
        item.hidden = !hit;
        if (hit) shown += 1;
      });

      group.hidden = shown === 0;
      visible += shown;
    });

    if (noMatch) noMatch.hidden = visible !== 0;
  }

  function elementFrom(event) {
    var node = event.target;
    return node && node.nodeType === 1 ? node : null;
  }

  document.addEventListener('click', function (event) {
    var target = elementFrom(event);
    if (!target) return;

    var toggleEl = target.closest('[data-brandnav-toggle]');

    if (toggleEl) {
      event.preventDefault();
      toggle(toggleEl.closest('[data-brandnav]'));
      return;
    }

    // A click anywhere outside an open bar menu closes it. The inline copy
    // inside the mobile menu stays put, since the whole drawer scrolls.
    Array.prototype.forEach.call(document.querySelectorAll('.brandnav--bar.brandnav--open'), function (root) {
      if (!root.contains(target)) close(root);
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;

    var openRoot = document.querySelector('.brandnav--open');
    if (!openRoot) return;

    close(openRoot);
    var toggleEl = openRoot.querySelector('[data-brandnav-toggle]');
    if (toggleEl) toggleEl.focus();
  });

  document.addEventListener('input', function (event) {
    var target = elementFrom(event);
    if (!target || !target.matches('[data-brandnav-filter]')) return;

    var root = target.closest('[data-brandnav]');
    if (root) filterBrands(root, target.value);
  });

  // Warm the cache once the page is idle so the first open feels instant.
  function prefetch() {
    if (!document.querySelector('[data-brandnav]')) return;
    fetchBrands().catch(function () {});
  }

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(prefetch, { timeout: 4000 });
  } else {
    window.setTimeout(prefetch, 2500);
  }
})();
